import { useState } from 'react';
import { usePages, usePage, useUpdatePage } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { copyText } from '@/lib/clipboard';

/**
 * "Add it to your site" (AB#617). The ONE embed mechanism that actually works is
 * the HTML comment marker `<!-- wpp:form slug="…" -->` (ssg/builder.rs
 * process_form_markers). The old `{% include "forms/….html" %}` advice was dead.
 *
 * The page-picker writes the marker straight into a page's stored content via the
 * pages API — the durable path (no editor round-trip to mangle the comment).
 */
export function AddToSitePanel({
  siteSlug,
  formSlug,
}: {
  siteSlug: string;
  formSlug: string;
}) {
  const marker = `<!-- wpp:form slug="${formSlug}" -->`;
  const { data: pagesData } = usePages(siteSlug);
  const pages = pagesData?.data ?? [];

  const [selectedPageId, setSelectedPageId] = useState('');
  const { data: pageData, isLoading: pageLoading } = usePage(siteSlug, selectedPageId);
  const updatePage = useUpdatePage(siteSlug);

  const copyMarker = async () => {
    if (await copyText(marker)) toast.success('Marker copied');
  };

  const addToPage = async () => {
    const page = pageData?.data;
    if (!page) return;
    if ((page.content ?? '').includes(marker)) {
      toast.info('That page already embeds this form');
      return;
    }
    try {
      const newContent = `${page.content ?? ''}\n${marker}\n`;
      await updatePage.mutateAsync({ id: page.id, data: { content: newContent } });
      toast.success(`Added to "${page.title}"`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update page');
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Add it to your site</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Drop this form onto a page, or paste the marker into any page or post where you
          want it to appear.
        </p>

        {/* Page picker — writes the marker into the chosen page's content */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-2">
            <Label htmlFor="add-to-page">Add to a page</Label>
            <Select value={selectedPageId} onValueChange={setSelectedPageId}>
              <SelectTrigger id="add-to-page" className="w-64">
                <SelectValue placeholder={pages.length ? 'Choose a page…' : 'No pages yet'} />
              </SelectTrigger>
              <SelectContent>
                {pages.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={addToPage}
            disabled={!selectedPageId || pageLoading || updatePage.isPending}
          >
            {updatePage.isPending ? 'Adding…' : 'Add to page'}
          </Button>
        </div>

        {/* Copyable marker (the marker, never the Tera include) */}
        <div className="space-y-2">
          <Label>Or paste this marker anywhere</Label>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-2 py-1 bg-muted rounded text-xs break-all">
              {marker}
            </code>
            <Button type="button" variant="secondary" size="sm" onClick={copyMarker}>
              Copy
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            The form renders where this marker sits when the site is built.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
